/**
 * Bundle Offer — Focus Gummies + Free Active Greens
 *
 * When a customer adds Focus Gummies (product ID: 15539651641727) to their cart
 * with a Recharge subscription, a free Active Greens product is automatically
 * added.  If the Focus Gummies are later removed from the cart the free Active
 * Greens is removed as well.
 */

import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/* -------------------------------------------------------------------------- */
/*  Constants                                                                 */
/* -------------------------------------------------------------------------- */

const FOCUS_GUMMIES_PRODUCT_ID = '15539651641727';
const FOCUS_GUMMIES_VARIANT_ID = '56904496316799';
const ACTIVE_GREENS_VARIANT_ID = '49361588224321';
const FREE_GIFT_PROPERTY_KEY = 'Free gift with subscription';
const FREE_GIFT_PROPERTY_VALUE = 'Focus Gummies';

/* -------------------------------------------------------------------------- */
/*  BundleOfferManager                                                        */
/* -------------------------------------------------------------------------- */

class BundleOfferManager {
	/** @type {boolean} */
	#adding = false;

	/** @type {boolean} */
	#syncing = false;

	/** @type {((...args: any[]) => Promise<Response>) | null} */
	#originalFetch = null;

	constructor() {
		document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
		this.#interceptCartChanges();
		this.#init();
	}

	/**
	 * Intercepts window.fetch to detect calls to /cart/change.js.
	 * After any cart change, syncs the free gift to match Focus Gummies state.
	 * This is the only reliable trigger when Recharge handles subscription
	 * removals (the standard cart:update event is suppressed).
	 */
	#interceptCartChanges() {
		this.#originalFetch = window.fetch;
		const self = this;

		window.fetch = function (...args) {
			const result = self.#originalFetch.apply(this, args);
			const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

			if (url.includes('/cart/change.js')) {
				result.then(() => {
					console.log('[bundle-offer] /cart/change.js call detected — syncing');
					self.#syncFreeGift();
				}).catch(() => { });
			}
			return result;
		};
	}

	/**
	 * Runs once on page load to ensure the free gift matches cart state.
	 */
	async #init() {
		if (!document.querySelector('cart-items-component')) return;

		try {
			const response = await fetch(`${Theme.routes.cart_url}.js`);
			if (!response.ok) return;

			const cart = await response.json();
			const hasFocusGummies = cart.items?.some(
				(/** @type {{ variant_id: number }} */ item) =>
					String(item.variant_id) === FOCUS_GUMMIES_VARIANT_ID,
			);
			const hasFreeGift = cart.items?.some(
				(/** @type {{ variant_id: number; properties?: Record<string, string> }} */ item) =>
					String(item.variant_id) === ACTIVE_GREENS_VARIANT_ID &&
					item.properties?.[FREE_GIFT_PROPERTY_KEY] === FREE_GIFT_PROPERTY_VALUE,
			);

			if (hasFocusGummies && !hasFreeGift) {
				await this.#addFreeGift();
			} else if (!hasFocusGummies && hasFreeGift) {
				const freeGiftIndex = cart.items?.findIndex(
					(/** @type {{ variant_id: number; properties?: Record<string, string> }} */ item) =>
						String(item.variant_id) === ACTIVE_GREENS_VARIANT_ID &&
						item.properties?.[FREE_GIFT_PROPERTY_KEY] === FREE_GIFT_PROPERTY_VALUE,
				);
				if (freeGiftIndex !== undefined && freeGiftIndex >= 0) {
					await this.#removeFreeGift(freeGiftIndex + 1);
				}
			}
		} catch {
			// Silently fail
		}
	}

	/* ------------------------------------------------------------------------ */
	/*  Event handlers                                                          */
	/* ------------------------------------------------------------------------ */

	/**
	 * Fires on every `cart:update` event (CartAddEvent, CartUpdateEvent, etc.).
	 * @param {Event & { detail?: { data?: { source?: string; productId?: string } } }} event
	 */
	#onCartUpdate = async (event) => {
		const detail = /** @type {Record<string, any>} */ (event.detail?.data || {});

		// Only handle product-form additions (removal is covered by fetch interceptor)
		if (detail.source === 'product-form-component') {
			console.log('[bundle-offer] CartAddEvent — productId:', detail.productId);

			if (detail.productId === FOCUS_GUMMIES_PRODUCT_ID) {
				await this.#addFreeGift();
			}
		}
	};

	/* ------------------------------------------------------------------------ */
	/*  Adding the free gift                                                    */
	/* ------------------------------------------------------------------------ */

	/**
	 * Fetches the cart, verifies Focus Gummies is present with a subscription
	 * selling plan, then adds the free Active Greens if not already in cart.
	 */
	async #addFreeGift() {
		if (this.#adding) return;
		this.#adding = true;

		try {
			const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
			if (!cartResponse.ok) return;

			const cart = await cartResponse.json();

			// Find the Focus Gummies line item (may not be present yet if event
			// fired before the cart was fully updated — bail gracefully)
			const focusGummiesLine = cart.items?.find(
				(/** @type {{ variant_id: number }} */ item) =>
					String(item.variant_id) === FOCUS_GUMMIES_VARIANT_ID,
			);

			if (!focusGummiesLine) {
				console.log('[bundle-offer] Focus Gummies not found in cart yet — skipping');
				return;
			}

			// Subscription detection via Shopify's standard selling_plan_allocation.
			// Works with Recharge, Shopify Subscriptions, and any other app that
			// uses Shopify selling plans.
			const isSubscription = !!focusGummiesLine.selling_plan_allocation;
			console.log('[bundle-offer] Focus Gummies subscription?', isSubscription);

			if (!isSubscription) {
				console.log('[bundle-offer] Not a subscription — skipping free gift');
				return;
			}

			// Avoid adding a duplicate free gift
			const alreadyInCart = cart.items?.some(
				(/** @type {{ variant_id: number; properties?: Record<string, string> }} */ item) =>
					String(item.variant_id) === ACTIVE_GREENS_VARIANT_ID &&
					item.properties?.[FREE_GIFT_PROPERTY_KEY] === FREE_GIFT_PROPERTY_VALUE,
			);
			if (alreadyInCart) {
				console.log('[bundle-offer] Free gift already in cart, skipping add');
				return;
			}

			console.log('[bundle-offer] Adding free Active Greens to cart…');

			// Build form-encoded body (mirrors what product-form.js sends)
			const formData = new FormData();
			formData.append('id', ACTIVE_GREENS_VARIANT_ID);
			formData.append('quantity', '1');
			formData.append(`properties[${FREE_GIFT_PROPERTY_KEY}]`, FREE_GIFT_PROPERTY_VALUE);

			// Include the cart section id so the response carries updated HTML
			const sectionId =
				document.querySelector('cart-items-component')?.dataset.sectionId || '';
			if (sectionId) {
				formData.append('sections', sectionId);
				formData.append('sections_url', window.location.pathname);
			}

			const response = await fetch(
				`${Theme.routes.cart_add_url}`,
				fetchConfig('javascript', { body: formData }),
			);

			const json = await response.json();

			if (json.status) {
				console.error('[bundle-offer] Cart add error:', json.message, json.description);
				return;
			}

			console.log('[bundle-offer] Free Active Greens added successfully');

			// Let other components know the cart changed (pass sections if available)
			document.dispatchEvent(
				new CustomEvent(ThemeEvents.cartUpdate, {
					bubbles: true,
					detail: {
						resource: {},
						sourceId: ACTIVE_GREENS_VARIANT_ID,
						data: {
							source: 'bundle-offer',
							sections: json.sections || {},
						},
					},
				}),
			);
		} catch (error) {
			console.error('[bundle-offer] Failed to add free Active Greens:', error);
		} finally {
			this.#adding = false;
		}
	}

	/* ------------------------------------------------------------------------ */
	/*  Syncing / removing the free gift                                        */
	/* ------------------------------------------------------------------------ */

	/**
	 * Fetches the full cart JSON and verifies that Focus Gummies is still
	 * present.  If it's gone, the free Active Greens line is removed as well.
	 */
	async #syncFreeGift() {
		// Guard against recursive calls (removeFreeGift also hits /cart/change.js)
		if (this.#syncing) return;
		this.#syncing = true;

		try {
			const response = await fetch(`${Theme.routes.cart_url}.js`);
			if (!response.ok) return;

			const cart = await response.json();

			const focusGummiesInCart = cart.items?.some(
				(/** @type {{ variant_id: number }} */ item) =>
					String(item.variant_id) === FOCUS_GUMMIES_VARIANT_ID,
			);

			// If Focus Gummies has been removed, clean up the free gift
			if (!focusGummiesInCart) {
				const freeGiftIndex = cart.items?.findIndex(
					(/** @type {{ variant_id: number; properties?: Record<string, string> }} */ item) =>
						String(item.variant_id) === ACTIVE_GREENS_VARIANT_ID &&
						item.properties?.[FREE_GIFT_PROPERTY_KEY] === FREE_GIFT_PROPERTY_VALUE,
				);

				if (freeGiftIndex !== undefined && freeGiftIndex >= 0) {
					await this.#removeFreeGift(freeGiftIndex + 1);
				}
			}
		} catch (error) {
			console.error('[bundle-offer] Failed to sync free gift:', error);
		} finally {
			this.#syncing = false;
		}
	}

	/**
	 * Removes a cart line item by its 1-based index.
	 * @param {number} line — 1-based cart line index to remove
	 */
	async #removeFreeGift(line) {
		try {
			console.log('[bundle-offer] Removing free Active Greens (line:', line, ')');
			const sectionId =
				document.querySelector('cart-items-component')?.dataset.sectionId || '';

			const body = JSON.stringify({
				line: line,
				quantity: 0,
				...(sectionId && {
					sections: sectionId,
					sections_url: window.location.pathname,
				}),
			});

			const response = await fetch(
				`${Theme.routes.cart_change_url}`,
				fetchConfig('json', { body }),
			);

			const json = await response.json();

			document.dispatchEvent(
				new CustomEvent(ThemeEvents.cartUpdate, {
					bubbles: true,
					detail: {
						resource: {},
						sourceId: String(line),
						data: {
							source: 'bundle-offer',
							sections: json.sections || {},
						},
					},
				}),
			);
		} catch (error) {
			console.error('[bundle-offer] Failed to remove free Active Greens:', error);
		}
	}
}

/* -------------------------------------------------------------------------- */
/*  Bootstrap                                                                 */
/* -------------------------------------------------------------------------- */

new BundleOfferManager();
